/**
 * Custom dropdown component for selecting registry artifact type configuration.
 * Uses a non-searchable Select to provide pure dropdown behavior similar to RegistryAccessOptions.
 */

// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {components, OptionProps, SingleValueProps} from 'react-select';

interface RegistryArtifactTypeOption {
  value: boolean;
  label: string;
  description: string;
}

const ARTIFACT_TYPE_OPTIONS: RegistryArtifactTypeOption[] = [
  {
    value: true,
    label: 'All types',
    description: 'Allow all artifact types in this registry',
  },
  {
    value: false,
    label: 'Specify types',
    description: 'Only allow specific artifact types in this registry',
  },
];

type RegistryArtifactTypesOptionsProps = {
  selectedArtifactTypeConfig: boolean | undefined;
  setSelectedArtifactTypeConfig: (allowAllTypes: boolean) => void;
  disabled?: boolean;
  onBlur?: () => void;
};

export const RegistryArtifactTypesOptions = ({
  selectedArtifactTypeConfig,
  setSelectedArtifactTypeConfig,
  disabled,
  onBlur,
}: RegistryArtifactTypesOptionsProps) => {
  const selectedOption = ARTIFACT_TYPE_OPTIONS.find(
    o => o.value === selectedArtifactTypeConfig
  );

  return (
    <Select<RegistryArtifactTypeOption>
      value={selectedOption}
      options={ARTIFACT_TYPE_OPTIONS}
      placeholder={'Select artifact types'}
      isSearchable={false}
      components={{
        Option: RegistryArtifactTypeOptionComp,
        SingleValue: RegistryArtifactTypeValueComp,
      }}
      onChange={(o: RegistryArtifactTypeOption | null) => {
        if (o != null) {
          setSelectedArtifactTypeConfig(o.value);
        }
      }}
      isDisabled={disabled}
      onBlur={onBlur}
    />
  );
};

const RegistryArtifactTypeOptionComp: React.FC<
  OptionProps<RegistryArtifactTypeOption, false>
> = props => {
  const {label, description} = props.data;

  return (
    <components.Option {...props}>
      <Tailwind>
        <div className="flex flex-col">
          <span
            data-test="registry-artifact-type-option"
            className="font-medium">
            {label}
          </span>
          <span className="text-sm text-moon-500">{description}</span>
        </div>
      </Tailwind>
    </components.Option>
  );
};

const RegistryArtifactTypeValueComp: React.FC<
  SingleValueProps<RegistryArtifactTypeOption, false>
> = ({children, ...props}) => {
  return (
    <components.SingleValue {...props}>
      <Tailwind>
        <span data-test="registry-artifact-type-value">{children}</span>
      </Tailwind>
    </components.SingleValue>
  );
};
